import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useAnalyticsSummary } from '../../graphql/queries';
import { PermissionGate } from '../../components/shared';
import { usePermission } from '../../hooks/usePermission';
import { downloadFile, ApiError } from '../../lib/apiClient';
import { useUsageRollupDashboard } from '../../hooks/useUsageRollupDashboard';
import type { TopCountEntry } from '../../lib/apiClient/usageAnalytics';
import type {
  PillarHealthScores,
  MaturityDistribution,
  AuditActivitySummary,
  EntityCountSummary,
  ActiveUsersByPeriod,
} from '../../graphql/types';
import '@tylertech/forge/card';
import '@tylertech/forge/button';
import './index.css';

const ForgeCard = 'forge-card' as React.ElementType;
const ForgeButton = 'forge-button' as React.ElementType;

export const Route = createFileRoute('/analytics/')({
  component: AnalyticsPage,
});

export { AnalyticsPage };

function scoreColorClass(score: number): string {
  if (score >= 75) return 'analytics-pillar-gauge__score--high';
  if (score >= 40) return 'analytics-pillar-gauge__score--medium';
  return 'analytics-pillar-gauge__score--low';
}

function SummaryCards({ entityCounts }: { entityCounts: EntityCountSummary }): JSX.Element {
  const cards = [
    { label: 'Services', count: entityCounts.services.total },
    { label: 'Capabilities', count: entityCounts.capabilities.total },
    { label: 'Data Domains', count: entityCounts.dataDomains.total },
    { label: 'Technologies', count: entityCounts.technologies.total },
  ];

  return (
    <div className="analytics-summary-cards">
      {cards.map(({ label, count }) => (
        <ForgeCard key={label} className="analytics-summary-card">
          <p className="analytics-summary-card__label">{label}</p>
          <p className="analytics-summary-card__count">{count.toLocaleString()}</p>
        </ForgeCard>
      ))}
    </div>
  );
}

function PillarGauges({ scores }: { scores: PillarHealthScores }): JSX.Element {
  const pillars = [
    { label: 'Application', data: scores.application },
    { label: 'Business', data: scores.business },
    { label: 'Information', data: scores.information },
    { label: 'Technology', data: scores.technology },
  ];

  return (
    <div className="analytics-pillar-gauges">
      {pillars.map(({ label, data }) => (
        <ForgeCard key={label} className="analytics-pillar-gauge">
          <p className="analytics-pillar-gauge__label">{label} Pillar</p>
          <p className={`analytics-pillar-gauge__score ${scoreColorClass(data.score)}`}>
            {data.score}%
          </p>
          <div
            className="analytics-pillar-gauge__bar-track"
            role="progressbar"
            aria-valuenow={data.score}
            aria-valuemin={0}
            aria-valuemax={100}
          >
            <div className="analytics-pillar-gauge__bar-fill" style={{ width: `${data.score}%` }} />
          </div>
          <ul className="analytics-pillar-gauge__factors">
            {data.factors.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        </ForgeCard>
      ))}
    </div>
  );
}

function MaturityChart({ distribution }: { distribution: MaturityDistribution }): JSX.Element {
  const levels = [
    { level: 1, count: distribution.level1 },
    { level: 2, count: distribution.level2 },
    { level: 3, count: distribution.level3 },
    { level: 4, count: distribution.level4 },
    { level: 5, count: distribution.level5 },
    { level: 6, count: distribution.level6 },
  ];

  const maxCount = Math.max(...levels.map((l) => l.count), 1);

  return (
    <ForgeCard className="analytics-maturity-chart">
      <div
        className="analytics-maturity-bars"
        role="img"
        aria-label="Maturity level distribution bar chart"
      >
        {levels.map(({ level, count }) => (
          <div key={level} className="analytics-maturity-bar-group">
            <span className="analytics-maturity-bar-group__count">{count}</span>
            <div
              className="analytics-maturity-bar-group__bar"
              style={{ height: `${(count / maxCount) * 120}px` }}
            />
            <span className="analytics-maturity-bar-group__label">L{level}</span>
          </div>
        ))}
      </div>
      <div className="analytics-maturity-chart__meta">
        <span>Total assessments: {distribution.totalAssessments}</span>
        <span>Average score: {distribution.averageScore.toFixed(1)}</span>
      </div>
    </ForgeCard>
  );
}

function AuditActivityCards({
  auditActivity,
}: {
  auditActivity: AuditActivitySummary;
}): JSX.Element {
  return (
    <div className="analytics-audit-activity">
      <ForgeCard className="analytics-audit-stat">
        <p className="analytics-audit-stat__label">Events (Last 30 Days)</p>
        <p className="analytics-audit-stat__value">
          {auditActivity.last30Days.total.toLocaleString()}
        </p>
      </ForgeCard>
      <ForgeCard className="analytics-audit-stat">
        <p className="analytics-audit-stat__label">Events (Last 90 Days)</p>
        <p className="analytics-audit-stat__value">
          {auditActivity.last90Days.total.toLocaleString()}
        </p>
      </ForgeCard>
      <ForgeCard className="analytics-audit-stat">
        <p className="analytics-audit-stat__label">Active Users (30 Days)</p>
        <p className="analytics-audit-stat__value">
          {auditActivity.activeUsers30Days.toLocaleString()}
        </p>
      </ForgeCard>
    </div>
  );
}

function AnalyticsPageSkeleton(): JSX.Element {
  return (
    <div className="analytics-skeleton">
      <div className="analytics-skeleton__row">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="analytics-skeleton__card" />
        ))}
      </div>
      <div className="analytics-skeleton__row">
        {[0, 1, 2, 3].map((i) => (
          <div key={i} className="analytics-skeleton__card" />
        ))}
      </div>
    </div>
  );
}

type ActiveUsersPeriod = 'lastWeek' | 'lastMonth' | 'lastQuarter' | 'lastYear';

const ACTIVE_USERS_PERIODS: { key: ActiveUsersPeriod; label: string }[] = [
  { key: 'lastWeek', label: 'Last Week' },
  { key: 'lastMonth', label: 'Last Month' },
  { key: 'lastQuarter', label: 'Last Quarter' },
  { key: 'lastYear', label: 'Last Year' },
];

function ActiveUsersSection({
  activeUsersByPeriod,
}: {
  activeUsersByPeriod: ActiveUsersByPeriod;
}): JSX.Element {
  const [activePeriod, setActivePeriod] = useState<ActiveUsersPeriod>('lastMonth');

  return (
    <div className="analytics-active-users">
      <div
        className="analytics-active-users__controls"
        role="group"
        aria-label="Select time period"
      >
        {ACTIVE_USERS_PERIODS.map(({ key, label }) => (
          <ForgeButton
            key={key}
            type="button"
            variant={activePeriod === key ? 'raised' : 'outlined'}
            onClick={() => setActivePeriod(key)}
            aria-pressed={activePeriod === key}
            data-testid={`active-users-period-${key}`}
          >
            {label}
          </ForgeButton>
        ))}
      </div>
      <ForgeCard className="analytics-active-users__card">
        <p className="analytics-audit-stat__label">
          Active Users — {ACTIVE_USERS_PERIODS.find((p) => p.key === activePeriod)?.label}
        </p>
        <p
          className="analytics-audit-stat__value"
          data-testid="active-users-count"
          aria-live="polite"
        >
          {/* activePeriod is constrained to ActiveUsersPeriod literal union — not user-controlled */}
          {/* eslint-disable-next-line security/detect-object-injection */}
          {activeUsersByPeriod[activePeriod].toLocaleString()}
        </p>
      </ForgeCard>
    </div>
  );
}

function formatDurationMs(ms: number): string {
  const totalSeconds = Math.round(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
}

function TopCountList({
  entries,
  emptyMessage,
}: {
  entries: TopCountEntry[];
  emptyMessage: string;
}): JSX.Element {
  if (entries.length === 0) {
    return <p className="analytics-usage-rollup__empty forge-typography--body2">{emptyMessage}</p>;
  }
  return (
    <ol className="analytics-usage-rollup__list">
      {entries.map((entry) => (
        <li key={entry.key} className="analytics-usage-rollup__list-item">
          <span className="analytics-usage-rollup__list-key">{entry.key}</span>
          <span className="analytics-usage-rollup__list-count">{entry.count.toLocaleString()}</span>
        </li>
      ))}
    </ol>
  );
}

function UsageRollupSection(): JSX.Element | null {
  const { data, loading, error } = useUsageRollupDashboard();

  if (loading) {
    return <AnalyticsPageSkeleton />;
  }

  if (error) {
    return (
      <p role="alert" className="forge-typography--body1">
        Failed to load usage rollup data. Please try again.
      </p>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <>
      <section className="analytics-page__section">
        <h2 className="analytics-page__section-title forge-typography--subtitle1">
          Active Users (DAU / WAU / MAU)
        </h2>
        <div className="analytics-audit-activity">
          <ForgeCard className="analytics-audit-stat">
            <p className="analytics-audit-stat__label">Daily Active Users</p>
            <p className="analytics-audit-stat__value" data-testid="usage-rollup-dau">
              {data.dailyActiveUsers.toLocaleString()}
            </p>
          </ForgeCard>
          <ForgeCard className="analytics-audit-stat">
            <p className="analytics-audit-stat__label">Weekly Active Users</p>
            <p className="analytics-audit-stat__value" data-testid="usage-rollup-wau">
              {data.weeklyActiveUsers.toLocaleString()}
            </p>
          </ForgeCard>
          <ForgeCard className="analytics-audit-stat">
            <p className="analytics-audit-stat__label">Monthly Active Users</p>
            <p className="analytics-audit-stat__value" data-testid="usage-rollup-mau">
              {data.monthlyActiveUsers.toLocaleString()}
            </p>
          </ForgeCard>
        </div>
      </section>

      <section className="analytics-page__section">
        <h2 className="analytics-page__section-title forge-typography--subtitle1">
          Feature &amp; Query Usage
        </h2>
        <div className="analytics-usage-rollup__grid">
          <ForgeCard className="analytics-usage-rollup__card">
            <p className="analytics-audit-stat__label">Top Routes</p>
            <TopCountList entries={data.topRoutes} emptyMessage="No route views recorded yet." />
          </ForgeCard>
          <ForgeCard className="analytics-usage-rollup__card">
            <p className="analytics-audit-stat__label">Top Features</p>
            <TopCountList
              entries={data.topFeatures}
              emptyMessage="No feature usage recorded yet."
            />
          </ForgeCard>
          <ForgeCard className="analytics-usage-rollup__card">
            <p className="analytics-audit-stat__label">Query Volume by Entity Type</p>
            <TopCountList
              entries={data.queryVolumeByEntityType}
              emptyMessage="No query activity recorded yet."
            />
          </ForgeCard>
        </div>
      </section>

      <section className="analytics-page__section">
        <h2 className="analytics-page__section-title forge-typography--subtitle1">
          Session Behavior
        </h2>
        <div className="analytics-audit-activity">
          <ForgeCard className="analytics-audit-stat">
            <p className="analytics-audit-stat__label">Sessions</p>
            <p className="analytics-audit-stat__value" data-testid="session-behavior-count">
              {data.sessionBehavior.sessionCount.toLocaleString()}
            </p>
          </ForgeCard>
          <ForgeCard className="analytics-audit-stat">
            <p className="analytics-audit-stat__label">Avg. Session Duration</p>
            <p className="analytics-audit-stat__value" data-testid="session-behavior-duration">
              {formatDurationMs(data.sessionBehavior.avgDurationMs)}
            </p>
          </ForgeCard>
          <ForgeCard className="analytics-audit-stat">
            <p className="analytics-audit-stat__label">Avg. Session Depth</p>
            <p className="analytics-audit-stat__value" data-testid="session-behavior-depth">
              {data.sessionBehavior.avgDepth.toFixed(1)}
            </p>
          </ForgeCard>
        </div>
        <ForgeCard className="analytics-usage-rollup__card analytics-usage-rollup__dropoff">
          <p className="analytics-audit-stat__label">Top Drop-off Routes</p>
          <TopCountList
            entries={data.sessionBehavior.topDropOffRoutes}
            emptyMessage="No session drop-off data recorded yet."
          />
        </ForgeCard>
      </section>
    </>
  );
}

function AnalyticsPage(): JSX.Element {
  const { data, loading, error } = useAnalyticsSummary();
  const canExportFull = usePermission('analytics:export');
  const [exportError, setExportError] = useState<string | null>(null);

  async function handleExportCsv(): Promise<void> {
    setExportError(null);
    try {
      await downloadFile('/analytics/export?format=csv&type=services', 'ea-export-services.csv');
    } catch (err) {
      setExportError(err instanceof ApiError ? err.message : 'Failed to export CSV.');
    }
  }

  async function handleExportJson(): Promise<void> {
    setExportError(null);
    try {
      await downloadFile('/analytics/export?format=json&type=full', 'ea-export-full.json');
    } catch (err) {
      setExportError(err instanceof ApiError ? err.message : 'Failed to export JSON.');
    }
  }

  return (
    <PermissionGate permission="analytics:view">
      <div className="analytics-page">
        <header className="analytics-page__header">
          <h1 className="analytics-page__title forge-typography--headline5">Analytics</h1>
          <p className="analytics-page__subtitle forge-typography--body2">
            Platform usage metrics, pillar health scores, and EA model statistics
          </p>
        </header>

        <div className="analytics-page__actions">
          <ForgeButton type="button" variant="outlined" onClick={handleExportCsv}>
            Export CSV
          </ForgeButton>
          {canExportFull && (
            <ForgeButton type="button" variant="outlined" onClick={handleExportJson}>
              Full Export (JSON)
            </ForgeButton>
          )}
        </div>

        {exportError && (
          <p role="alert" className="forge-typography--body1">
            {exportError}
          </p>
        )}

        {loading && <AnalyticsPageSkeleton />}

        {error && (
          <p role="alert" className="forge-typography--body1">
            Failed to load analytics data. Please try again.
          </p>
        )}

        {data?.analyticsQuery && (
          <>
            <section className="analytics-page__section">
              <h2 className="analytics-page__section-title forge-typography--subtitle1">
                Entity Counts
              </h2>
              <SummaryCards entityCounts={data.analyticsQuery.entityCounts} />
            </section>

            <section className="analytics-page__section">
              <h2 className="analytics-page__section-title forge-typography--subtitle1">
                Pillar Health Scores
              </h2>
              <PillarGauges scores={data.analyticsQuery.pillarHealthScores} />
            </section>

            <section className="analytics-page__section">
              <h2 className="analytics-page__section-title forge-typography--subtitle1">
                Maturity Distribution
              </h2>
              <MaturityChart distribution={data.analyticsQuery.maturityDistribution} />
            </section>

            <section className="analytics-page__section">
              <h2 className="analytics-page__section-title forge-typography--subtitle1">
                Audit Activity
              </h2>
              <AuditActivityCards auditActivity={data.analyticsQuery.auditActivity} />
            </section>

            <section className="analytics-page__section">
              <h2 className="analytics-page__section-title forge-typography--subtitle1">
                Active Users
              </h2>
              <ActiveUsersSection
                activeUsersByPeriod={data.analyticsQuery.auditActivity.activeUsersByPeriod}
              />
            </section>

            <UsageRollupSection />
          </>
        )}
      </div>
    </PermissionGate>
  );
}
